import { getTrendsCurveData } from "../../app/api/trendCurves";
import store from "../../app/store";
import {
  setTrendCurveCache,
  setTrendCurvePreferences,
  bumpTrendCurveRefresh,
  setPendingColorForLevel2,
} from "../../app/slices/trendSlice";
import { User } from "../../types";
import {
  filterActiveTrendCurvePeriods,
  getFactoredTrendCurveResponse,
  shouldPreserveAllCurvePeriods,
} from "./trendCurveDataUtils";
import { sanitizeTrendSessionFeatureFlags } from "../designerPage-utils";
import {
  getTrendFiltersForTrendCurve,
  getEffectiveTrendCurveFilters,
  getActiveTrendReqId,
  getTrendFiltersFromSession,
} from "./graphHelperUtils";
import {
  createTrendCurveSvgMarkup,
  TREND_CURVE_EXPANDED_CHART_WIDTH,
} from "./trendCurveChartRenderer";

import React from "react";
import {
  getTrendCurveWindowLabel,
  mappedFilterTypeTrendCurves,
  mappedTrendCurveWindow,
  normalizeTrendCurveWindowLabel,
  trendCurveWindows,
  TrendCurvesFilterType,
  TrendCurveWindowType,
} from "../../constants/constants";
import {
  TrendCurveChartData,
  TrendItem,
} from "../../interface/DesignerInterface";
import { filterArchivedGraphRows } from "../archivedTrends";

const emptyTrendCurveChart: TrendCurveChartData = {
  labels: [],
  datasets: [],
};

/** Stable key for L1 vs L2 color context — used in trend-curve cache invalidation. */
export const getTrendCurveColorContextKey = (
  filters: ReturnType<typeof getTrendFiltersForTrendCurve>
): string => {
  if (!filters) return "";
  return [
    filters.color_analysis_level ?? "",
    filters.selected_color ?? "",
  ].join("|");
};

type TrendCurveFetchResult = {
  trendCurveResponse: TrendItem[];
  factoredTrendCurveResponse: TrendCurveChartData | typeof emptyTrendCurveChart;
};

const inFlightTrendCurveRequests = new Map<
  string,
  Promise<TrendCurveFetchResult>
>();

const buildTrendCurveRequestSignature = (
  filterType: string,
  curveWindowType: string,
  reqId: string | undefined,
  colorContextKey: string
) => `${filterType}|${curveWindowType}|${reqId ?? ""}|${colorContextKey}`;

type TrendCurveWindowCacheEntry = {
  reqId: string;
  colorContextKey: string;
  rawData: TrendItem[];
  chartData: TrendCurveChartData;
};

/**
 * Multi-window trend-curve cache so toggling Curve Window is instant after each
 * window has been fetched once. Keyed by run (req_id) + colour context so a
 * re-run or filter change naturally misses and prunes stale entries.
 */
const trendCurveWindowCache = new Map<string, TrendCurveWindowCacheEntry>();

const getTrendCurveWindowCacheKey = (
  curveWindowType: TrendCurveWindowType
): string => {
  const reqId = getActiveTrendReqId();
  const colorContextKey = getTrendCurveColorContextKey(
    getEffectiveTrendCurveFilters()
  );
  return buildTrendCurveRequestSignature(
    TREND_CURVE_LIVE_FILTER_TYPE,
    curveWindowType,
    reqId,
    colorContextKey
  );
};

const pruneTrendCurveWindowCache = () => {
  const reqId = getActiveTrendReqId() ?? "";
  const colorContextKey = getTrendCurveColorContextKey(
    getEffectiveTrendCurveFilters()
  );
  trendCurveWindowCache.forEach((entry, key) => {
    if (entry.reqId !== reqId || entry.colorContextKey !== colorContextKey) {
      trendCurveWindowCache.delete(key);
    }
  });
};

/** Chart data for `curveWindowType` if already fetched for this run + colour context. */
export const getCachedTrendCurveChartForWindow = (
  curveWindowType: TrendCurveWindowType
): TrendCurveChartData | null => {
  pruneTrendCurveWindowCache();
  return (
    trendCurveWindowCache.get(getTrendCurveWindowCacheKey(curveWindowType))
      ?.chartData ?? null
  );
};

/**
 * When user picks an L1 color bubble, store pending L2 context for trend curves and
 * trigger a refetch only when the colour context actually changed.
 * Session is updated only on Run Analysis so the Run Analysis button stays enabled.
 */
export const syncColorSelectionForTrendCurves = (colorForLevel2: string) => {
  const previousKey = getTrendCurveColorContextKey(
    getEffectiveTrendCurveFilters()
  );

  store.dispatch(setPendingColorForLevel2(colorForLevel2 || null));

  const nextKey = getTrendCurveColorContextKey(getEffectiveTrendCurveFilters());
  if (nextKey !== previousKey) {
    store.dispatch(bumpTrendCurveRefresh());
  }
};

/** Live trend curves always use Most Popular until New & Emerging is reimplemented. */
export const TREND_CURVE_LIVE_FILTER_TYPE = "Most Popular" as const;

export const getStoredTrendCurveWindowLabel = (): TrendCurveWindowType => {
  try {
    const raw = sessionStorage.getItem("trends");
    if (!raw) return "Month";

    const trends = JSON.parse(raw) as { curve_window?: string };
    if (typeof trends.curve_window === "string") {
      return normalizeTrendCurveWindowLabel(
        getTrendCurveWindowLabel(trends.curve_window)
      );
    }
  } catch {
    // ignore session parse errors
  }

  return "Month";
};

export const persistTrendCurveWindowToSession = (
  curveWindowLabel: TrendCurveWindowType
) => {
  try {
    const raw = sessionStorage.getItem("trends");
    if (!raw) return;

    const trends = JSON.parse(raw) as Record<string, unknown>;
    trends.curve_window = mappedTrendCurveWindow[curveWindowLabel];
    sessionStorage.setItem("trends", JSON.stringify(trends));
  } catch {
    // ignore session write errors
  }
};

export const appendCurveWindowToTrendSessionParams = <
  T extends Record<string, unknown>,
>(
  params: T
): T & { curve_window?: string } => {
  const curveWindowLabel = getStoredTrendCurveWindowLabel();
  return {
    ...params,
    curve_window: mappedTrendCurveWindow[curveWindowLabel],
  };
};

export const getCachedTrendCurveChartData = (
  curveWindowType: TrendCurveWindowType
): TrendCurveChartData | null => {
  const cached = store.getState().trends.trendsData?.trendCurveCache;
  if (!cached) return null;

  const reqId = getActiveTrendReqId();
  const colorContextKey = getTrendCurveColorContextKey(
    getEffectiveTrendCurveFilters()
  );
  if (
    cached.filterType === TREND_CURVE_LIVE_FILTER_TYPE &&
    cached.curveWindowType === curveWindowType &&
    cached.req_id === reqId &&
    cached.colorContextKey === colorContextKey
  ) {
    return cached.chartData;
  }
  return null;
};

/** Replace analysis session with the current run; keep only the curve window. */
export const persistTrendAnalysisParamsToSession = (
  params: Record<string, unknown>
) => {
  const sanitizedParams = sanitizeTrendSessionFeatureFlags(params);
  // Colour filtering is a designer-posts concern; trend analysis never uses it.
  delete sanitizedParams.colors;
  sessionStorage.setItem(
    "trends",
    JSON.stringify(appendCurveWindowToTrendSessionParams(sanitizedParams))
  );
};

export const getLiveTrendCurveWindowLabel = (): TrendCurveWindowType => {
  const state = store.getState().trends.trendsData;
  const prefs = state?.trendCurvePreferences;
  const currentReqId = state?.req_id;

  if (
    prefs?.curveWindowType &&
    (!currentReqId || !prefs.req_id || prefs.req_id === currentReqId)
  ) {
    return normalizeTrendCurveWindowLabel(prefs.curveWindowType);
  }

  return getStoredTrendCurveWindowLabel();
};

export const getSnapshotTrendCurveWindowLabel = (
  graphsFilters?: { curve_window?: string } | null
): TrendCurveWindowType =>
  normalizeTrendCurveWindowLabel(
    getTrendCurveWindowLabel(graphsFilters?.curve_window)
  );

export const buildTrendCurvesPdfTableRows = (
  trendCurvesJSON: TrendCurveChartData
) =>
  [...trendCurvesJSON.datasets]
    .sort((a, b) => (a.tNo ?? 0) - (b.tNo ?? 0))
    .map(dataset => ({
      tNo: dataset.tNo ?? 0,
      trend: dataset.label,
      color: dataset.borderColor,
      values: dataset.data,
    }));

export const buildTrendCurvesPdfPayload = (
  curveWindowType: TrendCurveWindowType,
  trendCurvesJSON: TrendCurveChartData,
  trendCurvesUnfactoredJSON: TrendItem[]
) => ({
  filterType: TREND_CURVE_LIVE_FILTER_TYPE,
  curveWindowType,
  trendCurvesJSON,
  trendCurvesUnfactoredJSON,
  tableRows: buildTrendCurvesPdfTableRows(trendCurvesJSON),
  trendCurvesSvg: createTrendCurveSvgMarkup(
    trendCurvesJSON,
    TREND_CURVE_EXPANDED_CHART_WIDTH,
    { expanded: true }
  ),
});

/** Shape uploaded to S3 inside graphData.json → trendCurvesData for the PDF worker. */
export type TrendCurvesPdfPayload = ReturnType<
  typeof buildTrendCurvesPdfPayload
>;

export const persistTrendCurvePreferences = (
  curveWindowType: string,
  reqId: string
) => {
  const normalizedWindow = normalizeTrendCurveWindowLabel(curveWindowType);
  persistTrendCurveWindowToSession(normalizedWindow);

  if (!reqId) return;
  store.dispatch(
    setTrendCurvePreferences({
      filterType: TREND_CURVE_LIVE_FILTER_TYPE,
      curveWindowType: normalizedWindow,
      req_id: reqId,
    })
  );
};

export const generateTrendCurvesS3JSON = async (
  user: User | null
): Promise<TrendCurvesPdfPayload | null> => {
  try {
    const sessionFilters = getTrendFiltersFromSession();
    if (!sessionFilters?.apparel_type) {
      console.error("Trend curve PDF: apparel_type missing in session trends");
      return null;
    }

    if (!getActiveTrendReqId()) {
      console.error(
        "Trend curve PDF: req_id missing — run trend analysis first"
      );
      return null;
    }

    const curveWindowType = getLiveTrendCurveWindowLabel();
    const {
      trendCurveResponse: trendCurvesUnfactoredJSON,
      factoredTrendCurveResponse: trendCurvesJSON,
    } = await formatAndSetDataForTrendCurves(
      TREND_CURVE_LIVE_FILTER_TYPE,
      curveWindowType,
      user,
      undefined,
      undefined,
      { skipCache: true }
    );

    if (
      !(trendCurvesUnfactoredJSON && trendCurvesUnfactoredJSON.length) ||
      !("datasets" in trendCurvesJSON) ||
      !trendCurvesJSON.datasets.length
    ) {
      console.error("No Trend Curve Data for PDF");
      return null;
    }

    const chartData = trendCurvesJSON as TrendCurveChartData;

    return buildTrendCurvesPdfPayload(
      curveWindowType,
      chartData,
      trendCurvesUnfactoredJSON
    );
  } catch (error) {
    console.error("Error while generating trend curve PDF JSON", error, user);
    return null;
  }
};

export const formatAndSetDataForTrendCurves = async (
  _filterType: string,
  curveWindowType: string,
  user: User | null,
  setTrendsCurveData?: React.Dispatch<
    React.SetStateAction<TrendCurveChartData | null>
  >,
  setTrendsCurveRawData?: React.Dispatch<React.SetStateAction<TrendItem[]>>,
  options?: { skipCache?: boolean; background?: boolean }
) => {
  const filterType = TREND_CURVE_LIVE_FILTER_TYPE;
  const trendFilters = getEffectiveTrendCurveFilters();
  if (!trendFilters) {
    setTrendsCurveData?.(emptyTrendCurveChart);
    setTrendsCurveRawData?.([]);
    return { trendCurveResponse: [], factoredTrendCurveResponse: [] };
  }

  const state = store.getState();
  const cachedTrendCurve = state.trends.trendsData?.trendCurveCache;
  const archivedTrendNames = state.trends.archivedTrends;
  const currentReqId = getActiveTrendReqId();
  const colorContextKey = getTrendCurveColorContextKey(trendFilters);
  const requestSignature = buildTrendCurveRequestSignature(
    filterType,
    curveWindowType,
    currentReqId,
    colorContextKey
  );

  const preserveAllPeriods = shouldPreserveAllCurvePeriods(trendFilters);

  const applyArchivedFilter = (
    raw: TrendItem[]
  ): { rawData: TrendItem[]; chartData: TrendCurveChartData } => {
    const filteredRaw = filterArchivedGraphRows(raw, archivedTrendNames);
    const chartData = getFactoredTrendCurveResponse(
      filteredRaw,
      curveWindowType,
      undefined,
      preserveAllPeriods
    );
    return { rawData: filteredRaw, chartData };
  };

  if (!options?.skipCache) {
    pruneTrendCurveWindowCache();
    const cachedWindowEntry = trendCurveWindowCache.get(requestSignature);
    if (cachedWindowEntry) {
      const { rawData, chartData } = applyArchivedFilter(
        cachedWindowEntry.rawData
      );
      setTrendsCurveData?.(chartData);
      setTrendsCurveRawData?.(rawData);
      return {
        trendCurveResponse: rawData,
        factoredTrendCurveResponse: chartData,
      };
    }
  }

  if (
    !options?.skipCache &&
    cachedTrendCurve &&
    cachedTrendCurve.filterType === filterType &&
    cachedTrendCurve.curveWindowType === curveWindowType &&
    cachedTrendCurve.req_id === currentReqId &&
    cachedTrendCurve.colorContextKey === colorContextKey
  ) {
    const { rawData, chartData } = applyArchivedFilter(
      cachedTrendCurve.rawData
    );
    setTrendsCurveData?.(chartData);
    setTrendsCurveRawData?.(rawData);
    return {
      trendCurveResponse: rawData,
      factoredTrendCurveResponse: chartData,
    };
  }

  if (!options?.skipCache) {
    const inFlight = inFlightTrendCurveRequests.get(requestSignature);
    if (inFlight) {
      const result = await inFlight;
      setTrendsCurveData?.(result.factoredTrendCurveResponse);
      setTrendsCurveRawData?.(result.trendCurveResponse);
      return result;
    }
  }

  const setWindowCacheEntry = (
    rawData: TrendItem[],
    chartData: TrendCurveChartData
  ) => {
    trendCurveWindowCache.set(requestSignature, {
      reqId: currentReqId ?? "",
      colorContextKey,
      rawData,
      chartData,
    });
  };

  const fetchPromise = (async (): Promise<TrendCurveFetchResult> => {
    const response = await getTrendsCurveData(
      {
        ...trendFilters,
        query_type:
          mappedFilterTypeTrendCurves[filterType as TrendCurvesFilterType],
        curve_window:
          mappedTrendCurveWindow[curveWindowType as TrendCurveWindowType] ||
          "month",
      },
      user
    );
    const trendCurveResponse = response?.data.trendCurveData;

    if (Array.isArray(trendCurveResponse) && trendCurveResponse.length === 0) {
      if (currentReqId) {
        if (!options?.background) {
          store.dispatch(
            setTrendCurvePreferences({
              filterType,
              curveWindowType,
              req_id: currentReqId,
            })
          );
          store.dispatch(
            setTrendCurveCache({
              filterType,
              curveWindowType,
              req_id: currentReqId,
              colorContextKey,
              chartData: emptyTrendCurveChart,
              rawData: [],
            })
          );
        }
        setWindowCacheEntry([], emptyTrendCurveChart);
      }
      return {
        trendCurveResponse: [],
        factoredTrendCurveResponse: emptyTrendCurveChart,
      };
    }

    const trimmedRawData = filterActiveTrendCurvePeriods(
      trendCurveResponse,
      archivedTrendNames,
      preserveAllPeriods
    );
    const factoredTrendCurveResponse = getFactoredTrendCurveResponse(
      trimmedRawData,
      curveWindowType,
      undefined,
      preserveAllPeriods
    );

    if (currentReqId) {
      if (!options?.background) {
        store.dispatch(
          setTrendCurvePreferences({
            filterType,
            curveWindowType,
            req_id: currentReqId,
          })
        );
        store.dispatch(
          setTrendCurveCache({
            filterType,
            curveWindowType,
            req_id: currentReqId,
            colorContextKey,
            chartData: factoredTrendCurveResponse,
            rawData: trimmedRawData,
          })
        );
      }
      setWindowCacheEntry(trimmedRawData, factoredTrendCurveResponse);
    }

    return {
      trendCurveResponse: trimmedRawData,
      factoredTrendCurveResponse,
    };
  })();

  if (!options?.skipCache) {
    inFlightTrendCurveRequests.set(requestSignature, fetchPromise);
  }

  try {
    const result = await fetchPromise;
    setTrendsCurveData?.(result.factoredTrendCurveResponse);
    setTrendsCurveRawData?.(result.trendCurveResponse);
    return result;
  } finally {
    inFlightTrendCurveRequests.delete(requestSignature);
  }
};

/**
 * Fire-and-forget fetch of every other curve window so switching the dropdown
 * is instant. Only populates the multi-window cache — never touches the
 * single-slot Redux cache/preferences (those stay pinned to the visible window).
 */
export const prefetchTrendCurveWindows = (
  user: User | null,
  currentWindowType: TrendCurveWindowType
): void => {
  if (!getActiveTrendReqId()) return;
  for (const windowType of trendCurveWindows) {
    if (windowType === currentWindowType) continue;
    if (trendCurveWindowCache.has(getTrendCurveWindowCacheKey(windowType))) {
      continue;
    }
    void formatAndSetDataForTrendCurves(
      TREND_CURVE_LIVE_FILTER_TYPE,
      windowType,
      user,
      undefined,
      undefined,
      { background: true }
    ).catch(error => {
      console.error(
        "Background trend curve prefetch failed",
        error,
        windowType
      );
    });
  }
};
